import React, { useState, useEffect, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { XMarkIcon } from '@heroicons/react/24/outline'
import {
  GET_PRODUCT_VARIANTS_FOR_WIZARD,
  GET_VARIANT_OPTIONS_FOR_WIZARD,
  GET_BUNDLES_FOR_VARIANT_GROUPED
} from '../../graphql/product-queries'
import { Product, ProductVariant } from '../../types/product'
import { CartItem, WizardState } from '../../types/cart'
import ChooseVariantStep from './ChooseVariantStep'
import CustomizeStep from './CustomizeStep'
import MakeItAMealStep from './MakeItAMealStep'
import LoadingSpinner from '../LoadingSpinner'
import { optionItemSoldOut, withoutSoldOutOptions } from '../../lib/availability'
import { useSoldOutProducts } from '../../hooks/useProductAvailability'
import { useDropSoldOutOptions } from '../../hooks/useDropSoldOutOptions'

interface ProductWizardProps {
  product: Product
  isOpen: boolean
  onClose: () => void
  onAddToCart: (wizardState: WizardState) => void
  editItem?: CartItem
  editChildren?: CartItem[]
}

const ProductWizard: React.FC<ProductWizardProps> = ({
  product,
  isOpen,
  onClose,
  onAddToCart,
  editItem,
  editChildren = [],
}) => {
  const [wizardState, setWizardState] = useState<WizardState>({
    currentStep: 1,
    selectedProductId: Number(product.id),
    selectedVariantId: null,
    selectedOptions: {},
    selectedBundle: null,
    selectedMealParts: {}
  })

  // Step 1: Get product variants
  const { data: variantsData, loading: variantsLoading } = useQuery(
    GET_PRODUCT_VARIANTS_FOR_WIZARD,
    {
      variables: { productId: Number(product.id) },
      skip: !isOpen
    }
  )

  // Step 2: Get variant options (when variant is selected)
  const { data: optionsData, loading: optionsLoading, error: optionsError, refetch: refetchOptions } = useQuery(
    GET_VARIANT_OPTIONS_FOR_WIZARD,
    {
      variables: { variantId: Number(wizardState.selectedVariantId!) },
      skip: !wizardState.selectedVariantId || !isOpen
    }
  )

  // Step 3: Get eligible bundles grouped by product (when variant is selected)
  const { data: bundlesData, loading: bundlesLoading, error: bundlesError, refetch: refetchBundles } = useQuery(
    GET_BUNDLES_FOR_VARIANT_GROUPED,
    {
      variables: { variantId: Number(wizardState.selectedVariantId!) },
      skip: !wizardState.selectedVariantId || !isOpen
    }
  )

  // Map grouped response for the MakeItAMealStep
  const groupedMealData = useMemo(() => {
    const entries = bundlesData?.bundlesForEligibleVariantGrouped || []
    // We keep array of bundle products, each with groups, each with products (with product and options)
    return entries.map((entry: any) => ({
      bundle_product: { id: Number(entry.bundle_product.id), name: entry.bundle_product.name },
      groups: (entry.groups || []).map((grp: any) => ({
        id: Number(grp.id),
        name: grp.name,
        required_count: grp.required_count,
        sort_order: grp.sort_order,
        products: (grp.products || []).map((p: any) => ({
          product: { id: Number(p.product.id), name: p.product.name, sold_out: !!p.product.sold_out },
          options: (p.options || []).map((opt: any) => ({
            id: Number(opt.id),
            price_delta_cents: opt.price_delta_cents,
            sort_order: opt.sort_order,
            eligible_variant: {
              id: Number(opt.eligible_variant.id),
              name: opt.eligible_variant.name,
              price_cents: opt.eligible_variant.price_cents,
              // Statiegeld must survive this re-map: the enrichment below reads
              // it from here to build mealVariantDeposits, and dropping it
              // silently makes meal-deal drinks look deposit-free in the cart
              // while the API still charges them.
              deposit_cents: Number(opt.eligible_variant.deposit_cents || 0)
            }
          }))
        }))
      }))
    }))
  }, [bundlesData])

  // Only allow meal step when there is a bundle group named exactly "Main"
  // AND the selected variant is eligible within that Main group
  const mainEligibleBundles = useMemo(() => {
    const isMain = (name: any) => typeof name === 'string' && name.trim().toLowerCase() === 'main'
    if (!wizardState.selectedVariantId) return [] as any[]
    const selectedId = Number(wizardState.selectedVariantId)
    return (groupedMealData || []).filter((bp: any) =>
      (bp.groups || []).some((g: any) =>
        isMain(g.name) &&
        (g.products || []).some((p: any) => (p.options || []).some((opt: any) => Number(opt.eligible_variant?.id) === selectedId))
      )
    )
  }, [groupedMealData, wizardState.selectedVariantId])
  const showMealStep = !bundlesLoading && mainEligibleBundles.length > 0

  // Fetch the selected bundle product's variants when a bundle is chosen
  const { data: bundleVariantsData } = useQuery(
    GET_PRODUCT_VARIANTS_FOR_WIZARD,
    {
      variables: wizardState.selectedBundle ? { productId: Number(wizardState.selectedBundle.bundle_product_id) } : undefined as any,
      skip: !wizardState.selectedBundle || !isOpen
    }
  )
  const bundleProductVariants: Array<{ id: number; name: string; price_cents: number }> = useMemo(() => (
    (bundleVariantsData?.productVariants || []).map((v: any) => ({ id: Number(v.id), name: v.name, price_cents: Number(v.price_cents) }))
  ), [bundleVariantsData])

  // Default the bundle variant to the first available when not set yet
  useEffect(() => {
    if (!wizardState.selectedBundle) return
    if (!bundleProductVariants || bundleProductVariants.length === 0) return
    if (wizardState.selectedBundleVariantId) return
    setWizardState(prev => ({ ...prev, selectedBundleVariantId: bundleProductVariants[0].id }))
  }, [wizardState.selectedBundle, bundleProductVariants])

  const variants = variantsData?.productVariants?.map((variant: any) => ({
    ...variant,
    id: Number(variant.id)
  })) || []
  const productSoldOut = useSoldOutProducts()
  const optionGroupsWithItems = useMemo(() => (
    optionsData?.variantOptionGroupsByVariant?.map((group: any) => ({
      ...group,
      option_group: {
        ...group.option_group,
        id: Number(group.option_group.id),
        option_items: (group.option_group.option_items || []).map((item: any) => ({
          ...item,
          id: Number(item.id),
          // The linked product's live mark from the register's availability poll; the API's own
          // field is the fallback for a product the poll does not carry.
          sold_out: optionItemSoldOut(item, productSoldOut)
        }))
      }
    })) || []
  ), [optionsData, productSoldOut])
  
  // Determine if there are any customizable options for the selected variant
  const hasCustomizeStep = useMemo(() => {
    if (!wizardState.selectedVariantId) return false
    if (optionsLoading) return false
    return optionGroupsWithItems.some((group: any) => {
      const minSelect = Number(group.option_group?.min_select ?? 0)
      const hasItems = (group.option_group?.option_items || []).length > 0
      return minSelect > 0 || hasItems
    })
  }, [wizardState.selectedVariantId, optionsLoading, optionGroupsWithItems])

  // Pending steps — recomputed as data loads (used only until frozen)
  type StepId = 'choose' | 'customize' | 'meal'
  const pendingSteps: StepId[] = useMemo(() => {
    const isDefaultVariant = variants.length === 1 && variants[0].name === 'Default'
    const needsChoose = variants.length > 1 || (variants.length === 1 && !isDefaultVariant)
    const s: StepId[] = []
    if (needsChoose) s.push('choose')
    if (hasCustomizeStep) s.push('customize')
    if (showMealStep) s.push('meal')
    return s
  }, [variants, hasCustomizeStep, showMealStep])

  // Frozen steps — committed once and never changed again for this wizard session.
  // Prevents the step count from updating mid-flow as queries resolve.
  const [frozenSteps, setFrozenSteps] = useState<StepId[] | null>(null)

  // Reset when the wizard closes so it starts fresh next time
  useEffect(() => {
    if (!isOpen) setFrozenSteps(null)
  }, [isOpen])

  // Commit steps the moment the selected variant's data has fully loaded
  useEffect(() => {
    if (frozenSteps !== null) return
    if (!wizardState.selectedVariantId) return
    if (optionsLoading || bundlesLoading || optionsError || bundlesError) return
    setFrozenSteps(pendingSteps)
  }, [frozenSteps, wizardState.selectedVariantId, optionsLoading, bundlesLoading, optionsError, bundlesError, pendingSteps])

  // After freezing: auto-advance past the choose step if the user already selected a variant
  useEffect(() => {
    if (frozenSteps === null) return
    if (editItem) return
    if (wizardState.currentStep !== 1) return
    if (frozenSteps[0] !== 'choose') return
    if (!wizardState.selectedVariantId) return
    if (frozenSteps.length <= 1) return
    setWizardState(prev => ({ ...prev, currentStep: 2 }))
  }, [frozenSteps, editItem])

  const steps = frozenSteps ?? pendingSteps
  const currentStepId: StepId | undefined = steps[wizardState.currentStep - 1]

  // Auto-select variant if only one exists (skip in edit mode — variant is pre-set)
  useEffect(() => {
    if (editItem) return
    if (variants.length === 1 && !wizardState.selectedVariantId) {
      setWizardState(prev => ({
        ...prev,
        selectedVariantId: variants[0].id,
        currentStep: 1
      }))
    }
  }, [variants, wizardState.selectedVariantId, editItem])

  // Auto-add to cart when there are no steps needed (skip in edit mode — user must confirm)
  useEffect(() => {
    if (editItem) return
    // Also guard on errors: if a query errored (e.g. due to token expiry), the errorLink
    // is retrying in the background. Don't treat "no steps" as final until both queries
    // succeed — otherwise the product gets silently added with no customizations.
    const allLoaded = isOpen && !variantsLoading && !optionsLoading && !bundlesLoading && !optionsError && !bundlesError
    if (!allLoaded) return
    const hasNoSteps = steps.length === 0 && wizardState.selectedVariantId !== null
    if (hasNoSteps) {
      onAddToCart(wizardState)
      onClose()
    }
  }, [isOpen, steps, variantsLoading, optionsLoading, bundlesLoading, optionsError, bundlesError, wizardState.selectedVariantId, editItem])

  // Pre-populate selections from the cart item being edited
  useEffect(() => {
    if (!editItem || !isOpen) return
    const optionsRecord: Record<number, number[]> = {}
    for (const o of editItem.selected_options || []) {
      if (!optionsRecord[o.option_group_id]) optionsRecord[o.option_group_id] = []
      optionsRecord[o.option_group_id].push(o.option_item_id)
    }
    const mealParts: Record<number, number> = {}
    for (const child of editChildren) {
      if (child.type === 'meal_part' && child.bundle_group_id !== undefined) {
        mealParts[child.bundle_group_id] = child.product_variant_id
      }
    }
    const bundleChild = editChildren.find(c => c.type === 'bundle')
    setWizardState(prev => ({
      ...prev,
      selectedVariantId: editItem.product_variant_id,
      selectedOptions: optionsRecord,
      selectedOptionDetails: editItem.selected_options || [],
      selectedMealParts: mealParts,
      selectedBundle: bundleChild ? {
        bundle_group_id: bundleChild.bundle_group_id || 0,
        bundle_product_id: 0,
        bundle_product_name: bundleChild.product_name,
        price_delta_cents: bundleChild.price_cents,
      } : null,
      selectedBundleVariantId: bundleChild ? bundleChild.product_variant_id : undefined,
    }))
  }, [editItem, isOpen])

  // Jump to last step once steps are known in edit mode
  useEffect(() => {
    if (!editItem || !isOpen || steps.length === 0) return
    setWizardState(prev => ({ ...prev, currentStep: steps.length }))
  }, [editItem, isOpen, steps.length])

  // Once bundle data loads in edit mode, patch bundle_product_id and pre-select the Main group variant
  useEffect(() => {
    if (!editItem?.is_meal_deal || mainEligibleBundles.length === 0) return
    if (!wizardState.selectedBundle || wizardState.selectedBundle.bundle_product_id !== 0) return
    const bp = mainEligibleBundles[0]
    // Find the Main group so we can pre-select the original variant (it's hidden when pre-selected)
    const mainGroup = (bp.groups || []).find((g: any) =>
      typeof g.name === 'string' && g.name.toLowerCase().includes('main')
    )
    const mainGroupId = mainGroup ? Number(mainGroup.id) : null
    setWizardState(prev => ({
      ...prev,
      selectedBundle: prev.selectedBundle ? {
        ...prev.selectedBundle,
        bundle_product_id: bp.bundle_product.id,
        bundle_product_name: bp.bundle_product.name,
      } : null,
      selectedMealParts: mainGroupId ? {
        ...prev.selectedMealParts,
        [mainGroupId]: editItem.product_variant_id,
      } : prev.selectedMealParts,
    }))
  }, [editItem, mainEligibleBundles])

  // A meal-deal part that is sold out cannot stay chosen: its button is disabled, so a part
  // restored by the edit pre-fill, or one that sold out mid-wizard (the register's 30 s poll
  // updates the same Product entries), could not be deselected and would only fail at checkout.
  // Drop it so the step asks again. Main groups are skipped — that part is the product itself.
  useEffect(() => {
    const soldOutByGroup = new Map<number, Set<number>>()
    groupedMealData.forEach((bp: any) => {
      (bp.groups || []).forEach((grp: any) => {
        if (String(grp.name || '').toLowerCase().includes('main')) return
        const ids = new Set<number>()
        ;(grp.products || []).forEach((p: any) => {
          if (p.product?.sold_out) (p.options || []).forEach((o: any) => ids.add(Number(o.eligible_variant?.id)))
        })
        if (ids.size > 0) soldOutByGroup.set(Number(grp.id), ids)
      })
    })
    if (soldOutByGroup.size === 0) return
    setWizardState(prev => {
      const kept = Object.entries(prev.selectedMealParts).filter(([gid, vid]) => !soldOutByGroup.get(Number(gid))?.has(Number(vid)))
      return kept.length === Object.keys(prev.selectedMealParts).length ? prev : { ...prev, selectedMealParts: Object.fromEntries(kept) }
    })
  }, [groupedMealData, wizardState.selectedMealParts])

  // The twin rule for a customization option (see the hook): `OptionItem.sold_out` follows its
  // linked product's mark, derived from the register's availability poll. Drop it, with its
  // details, and go back to the customize step so it asks again.
  useDropSoldOutOptions(optionGroupsWithItems, wizardState.selectedOptions, soldOut => {
    const customizeStep = steps.indexOf('customize') + 1
    setWizardState(prev => ({
      ...prev,
      selectedOptions: withoutSoldOutOptions(prev.selectedOptions, soldOut),
      selectedOptionDetails: (prev.selectedOptionDetails || []).filter(d => !soldOut.has(Number(d.option_item_id))),
      currentStep: customizeStep > 0 ? customizeStep : prev.currentStep,
    }))
  })

  const handleVariantSelect = (variant: ProductVariant) => {
    // Reset frozen steps so they recompute for the new variant's data
    setFrozenSteps(null)
    setWizardState(prev => ({
      ...prev,
      selectedVariantId: variant.id,
      currentStep: 1,  // stay on choose step; auto-advance fires once steps are frozen
      selectedOptions: {},
      selectedBundle: null,
      selectedMealParts: {}
    }))
  }

  const handleOptionsChange = (optionGroupId: number, selectedItemIds: number[]) => {
    // Build selected option details for display and equality checks
    const details = (optionGroupsWithItems.find((g: any) => Number(g.option_group.id) === Number(optionGroupId))?.option_group?.option_items || [])
      .filter((it: any) => selectedItemIds.includes(Number(it.id)))
      .map((it: any) => ({
        option_group_id: Number(optionGroupId),
        option_group_name: String(optionGroupsWithItems.find((g: any) => Number(g.option_group.id) === Number(optionGroupId))?.option_group?.name || ''),
        option_item_id: Number(it.id),
        option_item_label: String(it.label || ''),
        price_delta_cents: Number(it.price_delta_cents || 0)
      }))

    setWizardState(prev => ({
      ...prev,
      selectedOptions: {
        ...prev.selectedOptions,
        [optionGroupId]: selectedItemIds
      },
      selectedOptionDetails: [
        ...(prev.selectedOptionDetails || []).filter(d => d.option_group_id !== Number(optionGroupId)),
        ...details
      ]
    }))
  }

  const handleBundleSelect = (bundle: any) => {
    setWizardState(prev => {
      // If the same bundle product is already selected, unselect it and clear its meal parts
      if (
        prev.selectedBundle &&
        prev.selectedBundle.bundle_product_id === bundle.bundle_product.id
      ) {
        // Remove only the meal parts that belong to this bundle product
        const newMealParts: Record<number, number> = {}
        groupedMealData.forEach((bp: any) => {
          const isSameProduct = bp.bundle_product?.id === bundle.bundle_product.id
          bp.groups?.forEach((grp: any) => {
            if (!isSameProduct && prev.selectedMealParts[grp.id] !== undefined) {
              newMealParts[grp.id] = prev.selectedMealParts[grp.id]
            }
          })
        })
        return {
          ...prev,
          selectedBundle: null,
          selectedMealParts: newMealParts
        }
      }

      // Otherwise select this bundle and preselect main when applicable
      let preselectedMealParts = { ...prev.selectedMealParts }
      if (prev.selectedVariantId) {
        // Find the selected bundle product in grouped data
        const bp = groupedMealData.find((x: any) => x.bundle_product?.id === bundle.bundle_product?.id)
        if (bp) {
          (bp.groups || []).forEach((grp: any) => {
            const isMain = typeof grp.name === 'string' && grp.name.toLowerCase().includes('main')
            if (!isMain) return
            const allOptions = (grp.products || []).flatMap((p: any) => p.options || [])
            const matching = allOptions.find((o: any) => Number(o.eligible_variant?.id) === Number(prev.selectedVariantId))
            if (matching) {
              preselectedMealParts[Number(grp.id)] = Number(prev.selectedVariantId)
            }
          })
        }
      }

      return ({
        ...prev,
        selectedBundle: {
          // Keep existing shape, but use representative price passed from MakeItAMealStep
          bundle_group_id: bundle.id,
          bundle_product_id: bundle.bundle_product.id,
          bundle_product_name: bundle.bundle_product.name,
          price_delta_cents: Number(bundle.representative_price_delta_cents || 0)
        },
        currentStep: 3,
        selectedMealParts: preselectedMealParts
      })
    })
  }

  const handleMealPartSelect = (bundleGroupId: number, variantId: number) => {
    setWizardState(prev => ({
      ...prev,
      selectedMealParts: {
        ...prev.selectedMealParts,
        [bundleGroupId]: variantId
      }
    }))
  }

  // Calculate total price including customizations
  const calculateTotalPrice = () => {
    if (!currentVariant) return 0
    
    let total = currentVariant.price_cents
    
    // Add customization surcharges
    Object.values(wizardState.selectedOptions).forEach(selectedItemIds => {
      selectedItemIds.forEach(itemId => {
        // Find the option item in any of the groups
        for (const group of optionGroupsWithItems) {
          const optionItem = (group.option_group.option_items || []).find((item: any) => item.id === itemId)
          if (optionItem) {
            total += optionItem.price_delta_cents
            break
          }
        }
      })
    })
    
    return total
  }

  // Every option group at or above its min_select. Checked on the customize step AND before the
  // final Add: a sold-out drop can empty a required group while the cashier is on a later step,
  // and the API does not validate min_select at order time.
  const optionsSatisfied = optionGroupsWithItems.every((group: any) => {
    const selectedItems = wizardState.selectedOptions[group.option_group.id] || []
    return selectedItems.length >= group.option_group.min_select
  })

  const handleNext = () => {
    if (wizardState.currentStep < steps.length) {
      setWizardState(prev => ({
        ...prev,
        currentStep: prev.currentStep + 1
      }))
    } else {
      if (!optionsSatisfied) {
        setWizardState(prev => ({ ...prev, currentStep: steps.indexOf('customize') + 1 }))
        return
      }
      // Enrich wizard state with human-readable names for bundle groups and selected meal parts
      const bundleGroupNames: Record<number, string> = {}
      const mealVariantNames: Record<number, { productName: string; variantName: string }> = {}
      // Statiegeld of each chosen meal part — a meal-deal drink is free but its
      // can is not, and the API charges it from the variant.
      const mealVariantDeposits: Record<number, number> = {}
      // Surcharge of each chosen meal part: it sits on the group option
      // (+€0,50 for a premium drink), not on the drink's variant.
      const mealVariantPriceDeltas: Record<number, number> = {}
      try {
        groupedMealData.forEach((bp: any) => {
          (bp.groups || []).forEach((grp: any) => {
            const gid = Number(grp.id)
            if (!bundleGroupNames[gid]) bundleGroupNames[gid] = String(grp.name || '')
            const selectedVid = wizardState.selectedMealParts[gid]
            if (selectedVid !== undefined) {
              // find matching option and its parent product name
              for (const p of (grp.products || [])) {
                const opt = (p.options || []).find((o: any) => Number(o.eligible_variant?.id) === Number(selectedVid))
                if (opt) {
                  mealVariantNames[gid] = {
                    productName: String(p.product?.name || ''),
                    variantName: String(opt.eligible_variant?.name || '')
                  }
                  mealVariantDeposits[gid] = Number(opt.eligible_variant?.deposit_cents || 0)
                  mealVariantPriceDeltas[gid] = Number(opt.price_delta_cents || 0)
                  break
                }
              }
            }
          })
        })
      // eslint-disable-next-line no-empty
      } catch {}

      const enrichedState = {
        ...wizardState,
        bundleGroupNames,
        mealVariantNames,
        mealVariantDeposits,
        mealVariantPriceDeltas
      }
      onAddToCart(enrichedState)
      onClose()
    }
  }

  const handleBack = () => {
    if (wizardState.currentStep > 1) {
      setWizardState(prev => ({
        ...prev,
        currentStep: prev.currentStep - 1
      }))
    }
  }

  const canProceed = () => {
    if (!currentStepId) return false
    if (currentStepId === 'choose') {
      return wizardState.selectedVariantId !== null
    }
    if (currentStepId === 'customize') {
      return optionsSatisfied
    }
    if (currentStepId === 'meal') {
      if (!wizardState.selectedBundle) return true
      const allGroups = groupedMealData.flatMap((bp: any) => bp.groups || [])
      return allGroups.every((grp: any) => {
        if (grp.required_count === 0) return true
        return wizardState.selectedMealParts[grp.id] !== undefined
      })
    }
    return false
  }

  // Suppress modal only while loading — not on error (we want to show an error state instead)
  const decidingSingleVariantFlow = (
    isOpen &&
    variants.length === 1 &&
    wizardState.selectedVariantId !== null &&
    (optionsLoading || bundlesLoading)
  )
  if (!isOpen || variantsLoading || decidingSingleVariantFlow) return null

  const currentVariant = variants.find((v: any) => v.id === wizardState.selectedVariantId)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {product.name}
              {currentVariant ? (
                <>
                  <span className="text-gray-400 mx-2">/</span>
                  <span className="text-gray-700">{currentVariant.name}</span>
                </>
              ) : null}
            </h2>
            {steps.length >= 2 && (
              <p className="text-sm text-gray-500">Step {wizardState.currentStep} of {steps.length}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Bar */}
        {steps.length >= 2 && (
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              {steps.map((_, idx) => {
                const stepNumber = idx + 1
                return (
                  <React.Fragment key={stepNumber}>
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                      stepNumber <= wizardState.currentStep
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {stepNumber}
                    </div>
                    {stepNumber < steps.length && (
                      <div className={`flex-1 h-1 mx-2 ${
                        stepNumber < wizardState.currentStep ? 'bg-primary-600' : 'bg-gray-200'
                      }`} />
                    )}
                  </React.Fragment>
                )
              })}
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              {steps.map((s, idx) => (
                <span key={idx}>{s === 'choose' ? 'Choose Variant' : s === 'customize' ? 'Customize' : 'Make it a Meal'}</span>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {frozenSteps === null && (optionsError || bundlesError) && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <p className="text-sm text-red-600 mb-4">Failed to load product options. Please try again.</p>
              <button
                onClick={() => { refetchOptions(); refetchBundles() }}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                Retry
              </button>
            </div>
          )}

          {currentStepId === undefined && (optionsLoading || bundlesLoading) && (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {currentStepId === 'choose' && (
            <ChooseVariantStep
              variants={variants}
              selectedVariantId={wizardState.selectedVariantId}
              onVariantSelect={handleVariantSelect}
              loading={variantsLoading}
            />
          )}

          {currentStepId === 'customize' && (
            <CustomizeStep
              optionGroups={optionGroupsWithItems}
              selectedOptions={wizardState.selectedOptions}
              onOptionsChange={handleOptionsChange}
              loading={optionsLoading}
            />
          )}

          {currentStepId === 'meal' && (
            <MakeItAMealStep
              eligibleBundles={mainEligibleBundles}
              selectedBundle={wizardState.selectedBundle}
              selectedMealParts={wizardState.selectedMealParts}
              onBundleSelect={handleBundleSelect}
              bundleVariants={bundleProductVariants}
              selectedBundleVariantId={wizardState.selectedBundleVariantId || null}
              onBundleVariantSelect={(_, variantId) => {
                setWizardState(prev => ({
                  ...prev,
                  selectedBundleVariantId: variantId
                }))
              }}
              onMealPartSelect={handleMealPartSelect}
              loading={bundlesLoading}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <button
            onClick={handleBack}
            disabled={wizardState.currentStep === 1}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Back
          </button>
          
          <div className="flex items-center space-x-4">
            {currentVariant && (
              <div className="text-right">
                <div className="text-lg font-semibold text-gray-900">
                  €{(calculateTotalPrice() / 100).toFixed(2)}
                </div>
                <div className="text-sm text-gray-500">
                  {currentVariant.name}
                  {calculateTotalPrice() !== currentVariant.price_cents && (
                    <span className="text-xs text-green-600 ml-1">
                      (+€{((calculateTotalPrice() - currentVariant.price_cents) / 100).toFixed(2)})
                    </span>
                  )}
                </div>
              </div>
            )}
            
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="px-6 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {wizardState.currentStep === steps.length ? (editItem ? 'Update Cart' : 'Add to Cart') : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductWizard
